/**
 * MultiPaperPrimarySelector — Phase 3-C (Slide 12, 3꼭지 "다음 1").
 *
 * 여러 paper 후보 중 어느 것이 응시자가 실제로 보는 paper(primary)인지
 * 시선 ray 교차 빈도로 결정. 5초 sliding window에서 응시자의 gaze
 * landing point가 각 paper bbox 안에 떨어진 횟수를 카운트.
 *
 * 슬라이드 의도: "동시 여러 paper 후보 중 어느 것이 응시자의 시험지
 * 인가? — 후보별 separate homography, 시선 ray 교차 빈도로 primary
 * 선택"
 *
 * 출력은 boolean이 아니라 hit count + confidence + 후보별 hits 분포로
 * 6원칙 #2 (evidence-first) 정합.
 */

export interface PaperCandidate {
  id: string;
  bbox: { x: number; y: number; w: number; h: number };
}

export interface PrimarySelection {
  /** 가장 많이 응시된 후보 id. 비었으면 null. */
  primaryId: string | null;
  /** 각 후보별 5s window hit count. */
  hitsById: Record<string, number>;
  /** primary가 다른 후보보다 명확하게 많이 응시됐는지 [0,1].
   *  hits 분포가 균등하면 낮음, 한 곳에 집중되면 1에 가까움. */
  confidence: number;
  /** 총 hit 샘플 수. */
  totalHits: number;
}

interface Sample { t: number; id: string | null }

export class MultiPaperPrimarySelector {
  private readonly windowMs: number;
  private samples: Sample[] = [];

  constructor(windowMs = 5000) {
    this.windowMs = windowMs;
  }

  reset(): void { this.samples = []; }

  /**
   * Push one gaze sample.
   * @param gaze landing point in normalised image coords, or null if no gaze.
   * @param candidates list of paper candidates this frame.
   * @param now timestamp ms.
   */
  update(
    gaze: { x: number; y: number } | null,
    candidates: PaperCandidate[],
    now: number,
  ): PrimarySelection {
    // Bucket the current landing into one of the candidates (or null).
    let hitId: string | null = null;
    if (gaze) {
      for (const c of candidates) {
        if (gaze.x >= c.bbox.x && gaze.x <= c.bbox.x + c.bbox.w
         && gaze.y >= c.bbox.y && gaze.y <= c.bbox.y + c.bbox.h) {
          hitId = c.id;
          break;
        }
      }
    }
    this.samples.push({ t: now, id: hitId });
    while (this.samples.length && now - this.samples[0].t > this.windowMs) this.samples.shift();

    const hitsById: Record<string, number> = {};
    for (const c of candidates) hitsById[c.id] = 0;
    let totalHits = 0;
    for (const s of this.samples) {
      if (s.id != null && hitsById[s.id] != null) {
        hitsById[s.id] += 1;
        totalHits += 1;
      }
    }

    let primaryId: string | null = null;
    let topHits = 0;
    for (const id of Object.keys(hitsById)) {
      if (hitsById[id] > topHits) { topHits = hitsById[id]; primaryId = id; }
    }

    // Confidence = (top - second) / total + min-sample bonus.
    // If only one paper or 80%+ of hits concentrated on the top, conf ≈ 1.
    let confidence = 0;
    if (totalHits > 0 && primaryId) {
      const second = Object.entries(hitsById)
        .filter(([id]) => id !== primaryId)
        .reduce((m, [, v]) => Math.max(m, v), 0);
      const margin = (topHits - second) / totalHits;
      const sampleScale = Math.min(1, totalHits / 10);  // need ≥10 samples for full conf
      confidence = Math.max(0, Math.min(1, margin * sampleScale));
    }

    return { primaryId, hitsById, confidence, totalHits };
  }
}
